<template>
  <v-dialog
    :value="isActive"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
    @keydown.esc="closeEditDialog"
  >
    <v-card tile>
      <v-toolbar max-height="64px" light color="grey lighten-4">
        <v-btn icon @click="closeEditDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          dark
          color="grey"
          @click="closeEditDialog"
          class="elevation-0 mr-2"
          plain
        >
          <v-icon class="mr-2">mdi-arrow-left</v-icon>
          Отмена
        </v-btn>
        <v-btn
          class="elevation-0 mr-2"
          dark
          color="orange"
          @click="discardChanges"
        >
          <v-icon class="mr-2">mdi-cancel</v-icon>
          Сброс
        </v-btn>
        <v-btn
          class="elevation-0"
          dark
          color="success"
          @click="saveChangesHandler"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Сохранить
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-container fluid>
          <v-row class="mt-3">
            <v-col cols="12" sm="6" md="3">
              <v-sheet class="elevation-2 pa-3">
                <template v-for="f in smallColumnFields">
                  <component
                    v-model="editingObject[f.value]"
                    rows="3"
                    :id="f.value"
                    :is="f.component"
                    :key="f.value"
                    :label="f.name"
                    :disabled="!f.editable"
                    :style="{ ...f.style }"
                    autocomplete="off"
                    @input="callCbWithCtx(f.onInput)"
                  >
                  </component>

                  <v-menu
                    v-if="f.selectBox"
                    :key="f.key"
                    :activator="`#${f.value}`"
                    transition="scale-transition"
                    nudge-bottom="35"
                    :close-on-content-click="true"
                    max-height="95vh"
                  >
                    <v-card>
                      <v-treeview
                        :items="f.selectLoadedData"
                        activatable
                        hoverable
                        item-text="title_ru"
                        return-object
                        @update:active="getActiveValue($event, f.value)"
                      ></v-treeview>
                    </v-card>
                  </v-menu>
                </template>
              </v-sheet>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="4"
              style="height: calc(100vh - 100px); overflow-y: scroll"
            >
              <v-sheet class="elevation-2 pa-3">
                <component
                  v-for="f in middleColumnFields"
                  v-model="editingObject[f.value]"
                  rows="3"
                  class="mb-3"
                  :is="f.component"
                  :key="f.value"
                  :style="f.style"
                  :label="f.name"
                  :disabled="!f.editable"
                  :placeholder="f.name"
                  @input="callCbWithCtx(f.onInput)"
                >
                </component>
              </v-sheet>
            </v-col>
            <v-col cols="12" sm="6" md="5">
              <edit-item-dialog-image
                :id="editingItemId"
                :type="type"
                :tableConfig="tableConfig"
                :pastedImageData="pastedImageData"
                @applyImage="applyImageHandler"
                @discardImage="discardImageHandler"
              ></edit-item-dialog-image>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { VTextField, VTextarea, VCheckbox } from "vuetify/lib";
import { VueEditor } from "vue2-editor";

import { DIALOG_TYPES } from "@/constants/common";
import EditItemDialogImage from "@/components/editItemDialog/EditItemDialogImage";

export default {
  components: {
    VTextField,
    VTextarea,
    VCheckbox,
    EditItemDialogImage,
    VueEditor,
  },
  props: {
    type: String,
    isActive: Boolean,
    tableSchema: Array,
    tableConfig: Object,
    dataObject: Object,
  },

  data() {
    return {
      inputComponent: VTextField,
      checkboxComponent: VCheckbox,
      textAreaComponent: VTextarea,
      pastedImageData: "",

      editingObject: {},

      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [
          { align: "left" },
          { align: "center" },
          { align: "right" },
          { align: "justify" },
        ],
        [{ color: "color-picker" }],
      ],

      listnerStatus: false,
    };
  },

  watch: {
    isActive() {
      if (this.type === DIALOG_TYPES.CREATE) {
        this.editingObject = this.getEmptyDataObject();
      }

      if (this.type === DIALOG_TYPES.EDIT) {
        this.editingObject = this.dataObject;
      }
    },
  },

  computed: {
    smallColumnFields() {
      return this.tableConfig.fields.filter(({ size }) => size === "sm");
    },

    middleColumnFields() {
      return this.tableConfig.fields.filter(({ size }) => size === "md");
    },

    toolbarTitle() {
      return this.type === DIALOG_TYPES.EDIT
        ? `${this.tableConfig.tableTitle}: редактирование`
        : `${this.tableConfig.tableTitle}: создание`;
    },

    editingItemId() {
      if (this.type === DIALOG_TYPES.CREATE) return null;
      if (!this.dataObject) return undefined;

      return this.dataObject.id;
    },
  },

  created() {
    if (!this.listnerStatus)
      window.addEventListener("paste", (e) => {
        this.listnerStatus = true;
        console.log("edit");
        const file = e.clipboardData.files[0];

        if (!file || !file.type.startsWith("image/") || !this.isActive) {
          return;
        }
        const reader = new FileReader();

        reader.onload = (e) => {
          this.pastedImageData = e.target.result;
        };

        reader.readAsDataURL(file);
      });

    Promise.all(
      this.tableConfig.fields
        .filter(({ selectBox }) => selectBox)
        .map((col) => col.fetchSelectData())
    );
  },

  beforeDestroy() {
    window.removeEventListener("paste", window);
  },

  methods: {
    getActiveValue(item, field) {
      this.editingObject[field] = item[0].id;
    },

    callCbWithCtx(cb) {
      cb.call(this);
    },

    applyImageHandler(file) {
      this.editingObject.file = file;
    },

    discardImageHandler() {
      this.editingObject.file = null;
    },

    getColsBySize(size) {
      const cols = this.tableConfig.schemaFieldSlots[size];

      return !cols
        ? []
        : this.tableSchema.filter(({ name }) =>
            this.tableConfig.schemaFieldSlots[size].includes(name)
          );
    },

    getEmptyDataObject() {
      const obj = {};

      this.tableSchema.map(({ name }) => {
        obj[name] = "";
      });

      // DEFAULT VALUES
      obj.publish = true;

      return obj;
    },

    getComponentByType(type) {
      switch (type) {
        case "text":
          return this.textAreaComponent;
        case "tinyint":
          return this.checkboxComponent;
        default:
          return this.inputComponent;
      }
    },

    closeEditDialog() {
      this.$emit("closeEditDialog");
    },

    saveChangesHandler() {
      this.$emit("saveChangesHandler", {
        data: this.editingObject,
        editType: this.type,
      });
    },

    discardChanges() {
      this.editingObject = this.getEmptyDataObject();
    },
  },
};
</script>
